import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/AdminSidebar";
import {
  Users,
  Search,
  Edit,
  Trash2,
  Shield,
  Crown,
  UserCheck,
  Building2,
  RefreshCw,
  Eye,
  Mail,
  Calendar,
  CreditCard,
  ChevronDown,
  Check,
  X,
  Gift,
  Percent,
  Star
} from "lucide-react";

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [tiers, setTiers] = useState(null);
  const [total, setTotal] = useState(0);
  const [specialAccessForm, setSpecialAccessForm] = useState({
    access_type: "ambassador",
    duration_months: 1,
    discount_percent: 50
  });

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchUsers();
    fetchTiers();
  }, [user]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append("role", roleFilter);
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "100");

      const res = await fetch(`${API}/admin/users?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API}/admin/subscription-tiers`, { credentials: "include" });
      if (res.ok) setTiers(await res.json());
    } catch (error) {
      console.error("Failed to fetch tiers");
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success("Role updated!");
        fetchUsers();
        setShowModal(false);
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const updateUserSubscription = async (userId, newTier) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/subscription`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ subscription_tier: newTier })
      });
      if (res.ok) {
        toast.success("Subscription updated!");
        fetchUsers();
        setShowModal(false);
      } else {
        toast.error("Failed to update subscription");
      }
    } catch (error) {
      toast.error("Failed to update subscription");
    }
  };

  const grantSpecialAccess = async (userId) => {
    try {
      const body = { access_type: specialAccessForm.access_type, duration_months: specialAccessForm.duration_months };
      if (specialAccessForm.access_type === "discount") {
        body.discount_percent = specialAccessForm.discount_percent;
      }
      const res = await fetch(`${API}/admin/users/${userId}/special-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(`Special access granted!`);
        fetchUsers();
        setShowModal(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to grant access");
      }
    } catch { toast.error("Failed to grant special access"); }
  };

  const revokeSpecialAccess = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/special-access`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast.success("Special access revoked");
        fetchUsers();
        setShowModal(false);
      } else {
        toast.error("Failed to revoke access");
      }
    } catch { toast.error("Failed to revoke special access"); }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin": return Crown;
      case "recruiter": return Building2;
      default: return UserCheck;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin": return "bg-yellow-500/20 text-yellow-400";
      case "recruiter": return "bg-blue-500/20 text-blue-400";
      default: return "bg-green-500/20 text-green-400";
    }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                USER <span className="text-crimson">MANAGEMENT</span>
              </h1>
              <p className="text-gray-400">Manage platform users, roles, and subscriptions</p>
            </div>
            <Button onClick={fetchUsers} variant="outline" className="border-[#333] text-white">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Users className="w-4 h-4" />
                <span className="text-xs">Total Users</span>
              </div>
              <div className="text-2xl font-bold">{total}</div>
            </div>
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Building2 className="w-4 h-4" />
                <span className="text-xs">Recruiters</span>
              </div>
              <div className="text-2xl font-bold text-blue-400">
                {users.filter(u => u.role === "recruiter").length}
              </div>
            </div>
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <UserCheck className="w-4 h-4" />
                <span className="text-xs">Job Seekers</span>
              </div>
              <div className="text-2xl font-bold text-green-400">
                {users.filter(u => u.role === "jobseeker").length}
              </div>
            </div>
            <div className="bg-obsidian-light border border-[#222] p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Crown className="w-4 h-4" />
                <span className="text-xs">Admins</span>
              </div>
              <div className="text-2xl font-bold text-yellow-400">
                {users.filter(u => u.role === "admin").length}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 flex-wrap mb-6">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
                className="pl-10 bg-obsidian-light border-[#333] text-white"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); }}
              className="px-4 py-2 bg-obsidian-light border border-[#333] text-white"
            >
              <option value="">All Roles</option>
              <option value="jobseeker">Job Seekers</option>
              <option value="recruiter">Recruiters</option>
              <option value="admin">Admins</option>
            </select>
            <Button onClick={fetchUsers} className="bg-crimson hover:bg-crimson/90">
              Search
            </Button>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="bg-obsidian-light border border-[#222] overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#333]">
                    <th className="text-left p-4 text-gray-400 font-medium">User</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Role</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Subscription</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Joined</th>
                    <th className="text-right p-4 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const RoleIcon = getRoleIcon(u.role);
                    return (
                      <tr key={u.user_id} className="border-b border-[#222] hover:bg-white/5">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                              {u.picture ? (
                                <img src={u.picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Users className="w-5 h-5 text-crimson" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-white">{u.name || "No name"}</div>
                              <div className="text-sm text-gray-500">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 text-xs uppercase flex items-center gap-1 w-fit ${getRoleColor(u.role)}`}>
                            <RoleIcon className="w-3 h-3" />
                            {u.role}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-gray-300 flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-gray-500" />
                            {u.subscription_tier || "free"}
                            {u.special_access?.active && u.special_access?.type === "ambassador" && (
                              <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 py-0.5 uppercase tracking-wider font-bold ml-1">Ambassador</span>
                            )}
                            {u.special_access?.active && u.special_access?.type === "discount" && (
                              <span className="bg-amber-500/20 text-amber-400 text-[10px] px-1.5 py-0.5 uppercase tracking-wider font-bold ml-1">{u.special_access.discount_percent}% off</span>
                            )}
                          </span>
                        </td>
                        <td className="p-4 text-gray-500 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#333] text-white"
                            onClick={() => { setSelectedUser(u); setShowModal(true); }}
                          >
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {users.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No users found matching your criteria
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Edit User Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 w-full max-w-md"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">Edit User</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-obsidian border border-[#333]">
                <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-crimson" />
                </div>
                <div>
                  <div className="font-medium text-white">{selectedUser.name}</div>
                  <div className="text-sm text-gray-500">{selectedUser.email}</div>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Role</label>
                <select
                  value={selectedUser.role}
                  onChange={(e) => updateUserRole(selectedUser.user_id, e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                >
                  <option value="jobseeker">Job Seeker</option>
                  <option value="recruiter">Recruiter</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Subscription Tier</label>
                <select
                  value={selectedUser.subscription_tier || ""}
                  onChange={(e) => updateUserSubscription(selectedUser.user_id, e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                >
                  <optgroup label="Job Seeker Tiers">
                    {tiers?.jobseeker?.map(t => (
                      <option key={t.id} value={t.id}>{t.name} (${t.price}/mo)</option>
                    ))}
                  </optgroup>
                  <optgroup label="Recruiter Tiers">
                    {tiers?.recruiter?.map(t => (
                      <option key={t.id} value={t.id}>{t.name} (${t.price}/mo)</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              
              <div className="pt-4 border-t border-[#333]">
                <p className="text-xs text-gray-500">
                  User ID: {selectedUser.user_id}
                </p>
                <p className="text-xs text-gray-500">
                  Joined: {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : "Unknown"}
                </p>
              </div>

              {/* Special Access Section */}
              <div className="pt-4 border-t border-[#333]">
                <label className="text-sm text-gray-400 mb-3 block flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-400" /> Special Access
                </label>

                {selectedUser.special_access?.active ? (
                  <div className="space-y-3">
                    <div className={`p-3 border ${selectedUser.special_access.type === "ambassador" ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-bold uppercase ${selectedUser.special_access.type === "ambassador" ? "text-emerald-400" : "text-amber-400"}`}>
                          {selectedUser.special_access.type === "ambassador" ? "Ambassador (Free Access)" : `${selectedUser.special_access.discount_percent}% Discount`}
                        </span>
                      </div>
                      {selectedUser.special_access.expires_at && (
                        <p className="text-xs text-gray-500">Expires: {new Date(selectedUser.special_access.expires_at).toLocaleDateString()}</p>
                      )}
                      {!selectedUser.special_access.expires_at && (
                        <p className="text-xs text-gray-500">No expiration set</p>
                      )}
                    </div>
                    <Button
                      onClick={() => revokeSpecialAccess(selectedUser.user_id)}
                      variant="outline"
                      size="sm"
                      className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="revoke-special-access"
                    >
                      <X className="w-3 h-3 mr-1" /> Revoke Special Access
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <select
                      value={specialAccessForm.access_type}
                      onChange={(e) => setSpecialAccessForm(f => ({ ...f, access_type: e.target.value }))}
                      className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                      data-testid="special-access-type"
                    >
                      <option value="ambassador">Ambassador (Free Access)</option>
                      <option value="discount">Custom Discount</option>
                    </select>

                    {specialAccessForm.access_type === "discount" && (
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Discount %</label>
                        <input
                          type="number"
                          min="1" max="99"
                          value={specialAccessForm.discount_percent}
                          onChange={(e) => setSpecialAccessForm(f => ({ ...f, discount_percent: parseInt(e.target.value) || 50 }))}
                          className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                          data-testid="discount-percent-input"
                        />
                      </div>
                    )}

                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Duration</label>
                      <select
                        value={specialAccessForm.duration_months}
                        onChange={(e) => setSpecialAccessForm(f => ({ ...f, duration_months: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                        data-testid="special-access-duration"
                      >
                        <option value={1}>1 Month</option>
                        <option value={3}>3 Months</option>
                        <option value={6}>6 Months</option>
                        <option value={12}>1 Year</option>
                      </select>
                    </div>

                    <Button
                      onClick={() => grantSpecialAccess(selectedUser.user_id)}
                      size="sm"
                      className="w-full bg-crimson hover:bg-crimson/80 text-white"
                      data-testid="grant-special-access"
                    >
                      <Gift className="w-3 h-3 mr-1" /> Grant Special Access
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowModal(false)}
                variant="outline"
                className="flex-1 border-[#333] text-white"
              >
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
